// src/hooks/useSubmitForm.jsx
import { useCallback, useRef, useState } from "react";
import api from "../config/axiosConfig";
import getCookie from "../utils/utils";
import { validate } from "../utils/validation";
import { toFormErrors } from "../utils/apiErrors";
import { toEnglishDigits } from "../utils/convertNumbers";

const methodMap = {
  POST: (url, data, cfg) => api.post(url, data, cfg),
  PUT: (url, data, cfg) => api.put(url, data, cfg),
  PATCH: (url, data, cfg) => api.patch(url, data, cfg),
  DELETE: (url, data, cfg) => api.delete(url, { data, ...cfg }),
};


export default function useSubmitForm(
  initialState,
  submitUrl,
  httpMethod = "POST",
  afterSubmit,          // optional fallback callback (was unused before)
  instanceConfig        // used by validation + postSubmit fallback
) {
  const [formData, setFormData] = useState(initialState);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const inFlight = useRef(null);

  const farsiOnlyFields = instanceConfig?.farsiOnlyFields || [];
  const englishOnlyFields = instanceConfig?.englishOnlyFields || [];

  const resetForm = useCallback(() => {
    setFormData(initialState);
  }, [initialState]);

  const handleChange = (arg1, arg2) => {
    let name, value, currentTarget = null;

    if (typeof arg1 === "string") {
      name = arg1;
      value = arg2;
    } else if (arg1 && typeof arg1 === "object" && "target" in arg1) {
      const ev = arg1;
      currentTarget = ev.currentTarget || null;

      if (typeof arg2 === "boolean" && ev.target && (ev.target.type === "checkbox" || ev.target.role === "switch")) {
        name = ev.target.name;
        value = arg2;
      } else {
        name = ev.target.name;
        value = ev.target.value;
      }
    } else {
      return;
    }

    if (!name) {
      return;
    }

    const keys = name.split(".");
    const lastKey = keys[keys.length - 1];

    // Handle digits flag normalization
    if (instanceConfig?.fieldConfigs && instanceConfig.fieldConfigs[lastKey]?.digits === 'fa-ui/en-wire') {
      value = toEnglishDigits(value);
    }

    if (farsiOnlyFields.includes(lastKey) && value && !instanceConfig?.isFarsiInput?.(value)) {
      // Set field error to show message under the field
      setErrors(prev => ({
        ...prev,
        [name]: "لطفاً از فونت فارسی استفاده کنید"
      }));
      setSnackbarMessage("برای این بخش لطفا از فونت فارسی استفاده کنید");
      if (currentTarget) setAnchorEl(currentTarget);
      return;
    }
    if (englishOnlyFields.includes(lastKey) && value && !instanceConfig?.isEnglishInput?.(value)) {
      // Set field error to show message under the field
      setErrors(prev => ({
        ...prev,
        [name]: "لطفاً از فونت لاتین استفاده کنید"
      }));
      setSnackbarMessage("برای این بخش لطفا از فونت لاتین استفاده کنید");
      if (currentTarget) setAnchorEl(currentTarget);
      return;
    }
    
    // Clear field error if validation passes
    if (farsiOnlyFields.includes(lastKey) || englishOnlyFields.includes(lastKey)) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    
    setAnchorEl(null);

    setFormData((prev) => {
      const next = { ...prev };
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const toFormData = (obj, fd = new FormData(), prefix = "") => {
    Object.entries(obj || {}).forEach(([k, v]) => {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v instanceof File || v instanceof Blob) {
        fd.append(key, v);
      } else if (Array.isArray(v)) {
        v.forEach((item, idx) => {
          const arrKey = `${key}[${idx}]`;
          if (item instanceof File || item instanceof Blob) fd.append(arrKey, item);
          else if (item && typeof item === "object") toFormData(item, fd, arrKey);
          else fd.append(arrKey, item ?? "");
        });
      } else if (v && typeof v === "object") {
        toFormData(v, fd, key);
      } else {
        // Keep null/undefined as "omit the field" in multipart to avoid DRF "" issues.
        if (v === null || v === undefined) return;
        fd.append(key, v);
      }
    });
    return fd;
  };

  /**
   * Submit handler
   * @param {Event} event
   * @param {(ctx:{resetForm:Function,setSnackbar:Function})=>void} onAfterSuccess optional per-call callback
   */

  // --- Debug helpers (keep for future)
  // Dump FormData to a plain object for logging
  const dumpFormData = (fd) => {
    const out = {};
    for (const [k, v] of fd.entries()) {
      out[k] = v instanceof File ? `[File:${v.name}]` : v;
    }
    return out;
  };

  const handleSubmit = async (event, onAfterSuccess) => {
    if (event) event.preventDefault();

    // cancel previous
    inFlight.current?.abort?.();
    const controller = new AbortController();
    inFlight.current = controller;

    setIsLoading(true);
    setOpenSnackbar(false);

    // Validate
    const newErrors = validate(formData, instanceConfig);
    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      setIsLoading(false);
      return;
    }

    // Prepare payload (allow config to prune/normalize)
    const cleaned = instanceConfig?.transformBeforeSubmit
      ? instanceConfig.transformBeforeSubmit(formData)
      : formData;
    const body = toFormData(cleaned);

    const config = {
      withCredentials: true,
      signal: controller.signal,
    };

    const methodFn = methodMap[httpMethod.toUpperCase()] || methodMap.POST;


    try {
      const res = await methodFn(submitUrl, body, config);
      

      // choose callback priority: per-call > hook arg > instanceConfig.postSubmit
      const cb =
        onAfterSuccess ||
        afterSubmit ||
        instanceConfig?.postSubmit;

      if (cb) {
        // Create a properly bound setSnackbar function
        const boundSetSnackbar = (msg) => {
          setSnackbarMessage(msg || "");
          setOpenSnackbar(Boolean(msg));
        };
        
        const callbackParams = {
          resetForm,
          setSnackbar: boundSetSnackbar,
        };
        
        try {
          cb(callbackParams);
        } catch (error) {
          // Error executing callback
        }
      } else {
        // fallback UX if no callback provided
        setSnackbarMessage("داده ها با موفقیت ثبت گردید!");
        setOpenSnackbar(true);
        // optionally reset:
        // resetForm();
      }

      setErrors({});
    } catch (error) {
      if (controller.signal.aborted) return;


      const fieldErrors = toFormErrors(error);
      setErrors(fieldErrors);
      setSnackbarMessage(fieldErrors.__all__ || "مشکلی پیش آمده است!");
      setOpenSnackbar(true);
      setAnchorEl(event?.currentTarget || null);

    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  return {
    formData,
    handleChange,
    handleSubmit,          // now accepts (e, onAfterSuccess)
    handleCloseSnackbar,
    openSnackbar,
    snackbarMessage,
    errors,
    setSnackbarMessage,
    setErrors,
    anchorEl,
    setAnchorEl,
    isLoading,
  };
}
